import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import { MetaTagDescriptor } from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { useScrollTo } from "@hooks/useScrollTo";
import { Route } from "@route-types/Thrive/Events/+types/ThriveEventsPage";
import ThriveNavigation from "@routes/Section/layouts/Thrive/components/ThriveNavigation";
import ThriveNewsletter from "@routes/Section/layouts/Thrive/components/ThriveNewsLetter";
import ThriveSocialMediaBanner from "@routes/Section/layouts/Thrive/components/ThriveSocialMediaBanner";
import ThriveTopBanner from "@routes/Section/layouts/Thrive/components/ThriveTopBanner";
import { fetchThriveEventsPageData } from "@routes/Thrive/Events/ThriveEventsPage.server";
import { THRIVE_EVENTS_GA_PROPS } from "@routes/Thrive/Events/utils/contants";
import { ThriveEventssPageContext } from "@routes/Thrive/Events/utils/types";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { sectionNavigationItems } from "@util/constant";

import ThriveBanner from "./components/ThriveBanner";
import ThrivePastEvents from "./components/ThrivePastEvents";

const ThriveEventsPage = (): React.ReactElement => {
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();

  useInjectClientData({ gaProps: THRIVE_EVENTS_GA_PROPS });

  const [scrollToRef, setShouldScrollTo] = useScrollTo<HTMLDivElement>();

  return (
    <div className="w-full" data-testid="thrive-events-page">
      <Ad
        adType="lb1"
        className="flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[250px]"
      />

      <div ref={breadcrumbsRef}></div>

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory={RouteFactory.thrive.replace("/", "")}
        childCategory={RouteFactory.thriveEvents.replace("/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <Container>
        <ThriveTopBanner />

        <div className="mt-4 flex flex-col-reverse gap-6 lg:mt-8 lg:flex-row">
          <div className="w-full lg:w-8/12">
            <ThriveNavigation />

            <ThriveBanner setShouldScrollTo={setShouldScrollTo} />

            <ThrivePastEvents scrollToRef={scrollToRef} />
          </div>

          <div className="border-b border-green-100 lg:mb-6 lg:mt-18 lg:block lg:w-4/12 lg:border-b-0">
            <ThriveSocialMediaBanner />

            <ThriveNewsletter />

            <div className="mb-6 mt-2 hidden lg:block lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
              />
            </div>
          </div>
        </div>
      </Container>

      <PulseEntry />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default ThriveEventsPage;

export const loader = async (): Promise<ThriveEventssPageContext> => {
  return await fetchThriveEventsPageData();
};

export const meta = ({ loaderData }: Route.MetaArgs): MetaTagDescriptor[] => {
  const context = loaderData as ThriveEventssPageContext;

  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return data.metaTags;
};
